/**
 * The Floor Plan step's machine card host (`floor-plan-page-machine-card`, `floor-plan-item-cards`).
 *
 * Renders `@repo/ui`'s `MachineCard` for the selected machine and binds what only the page can:
 * - the identify write behind *Change product* on a row with no product;
 * - the product strip's turning 3D model (`MachineModelPreview`, R16);
 * - the sub line's variation (R6), from the shared catalogue store, which it asks to load once
 *   if nothing has yet (the read the title-bar search and the Library share).
 *
 * The identify write is the Equipment step's own binding (`useRegisterIdentifyBinding`) and the
 * same `useIdentifyGroup` the identify card uses, so a one-machine identify writes, toasts and undoes
 * exactly as a group's does — into the plan's toast region. A row that already has a product is changed by the card itself (`PATCH reference_id`).
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useManufacturerCatalogStore } from '@repo/front-end-state';
import { MachineCard, type MachineProductHost } from '@repo/ui/planner';
import { planIdentifyToastSub, useIdentifyGroup } from '@repo/ui';
import Config from '../../common/Config';
import { MachineModelPreview } from '../../components/3D/planView/MachineModelPreview';
import { useRegisterIdentifyBinding } from './EquipmentIdentify';
import { usePlanIdentifyFetchers, type FloorPlanIdentifyProps } from './FloorPlanIdentify';

export interface FloorPlanMachineCardProps
  extends Pick<
    FloorPlanIdentifyProps,
    | 'twinId'
    | 'baseUrl'
    | 'getToken'
    | 'currentTwinId'
    | 'reloadRegister'
    | 'beginRegisterWrite'
    | 'settleRegisterWrite'
  > {
  /** The twin's dGraph BID (`?twinBid=`). */
  twinBid?: string;
  /** The plan's toast region (`PLAN_TOAST_REGION`). */
  toastRegion: string;
}

export const FloorPlanMachineCard: React.FC<FloorPlanMachineCardProps> = ({
  twinId,
  twinBid,
  baseUrl,
  getToken,
  currentTwinId,
  reloadRegister,
  beginRegisterWrite,
  settleRegisterWrite,
  toastRegion,
}) => {
  // The binding's map of groups a write has just landed on. The card writes one row at a time and
  // moves on to nothing, so nothing reads it; the binding requires one.
  const settling = useRef(new Map<string, readonly number[]>());
  const { api, fetchers } = useRegisterIdentifyBinding({
    twinId,
    baseUrl,
    getToken,
    currentTwinId,
    reloadRegister,
    beginRegisterWrite,
    settleRegisterWrite,
    settling,
  });
  /** The product the last press chose, for its toast's second line. */
  const pressed = useRef<Parameters<typeof planIdentifyToastSub>[0]>(undefined);
  const machineApi = useMemo(
    () => ({
      ...api,
      toastRegion,
      toastSub: () => planIdentifyToastSub(pressed.current),
    }),
    [api, toastRegion],
  );
  const writes = useIdentifyGroup(machineApi);
  const planFetchers = usePlanIdentifyFetchers(baseUrl, getToken);

  const { busy, identify } = writes;
  const product = useMemo<MachineProductHost>(
    () => ({
      fetchers,
      planFetchers,
      identifyBusy: busy,
      logContext: twinId !== null ? { twinId } : undefined,
      identifyRow: (rowId, referenceId, label, chosen) => {
        pressed.current = chosen;
        return identify({ label }, referenceId, [rowId], failed => failed);
      },
    }),
    [fetchers, planFetchers, busy, identify, twinId],
  );

  /*
   * The catalogue, read once per session and shared (`useManufacturerCatalogStore`): asked for here
   * only while nothing has read it, the call the title-bar search makes (`usePlanSearchSources`). The
   * store's own guard shares a read in flight and keeps a successful one.
   */
  const { getAccessTokenSilently } = useAuth0();
  const catalogueStatus = useManufacturerCatalogStore(s => s.status);
  const loadCatalogue = useManufacturerCatalogStore(s => s.load);
  const byReference = useManufacturerCatalogStore(s => s.byReference);
  useEffect(() => {
    if (catalogueStatus !== 'idle') return;
    void loadCatalogue(Config.middlewareApi, () => getAccessTokenSilently());
  }, [catalogueStatus, loadCatalogue, getAccessTokenSilently]);

  /** The sub line's variation: the catalogue's `equipment_variation`, else its `equipment_type`. */
  const variationOf = useCallback(
    (referenceId: string): string | undefined => {
      const entry = byReference.get(referenceId);
      return entry?.equipment_variation?.trim() || entry?.equipment_type?.trim() || undefined;
    },
    [byReference],
  );
  const modelPreview = useCallback(
    (file: string | null) => <MachineModelPreview modelFile={file} />,
    [],
  );

  return (
    <MachineCard
      twinBid={twinBid}
      twinId={twinId}
      product={product}
      toastRegion={toastRegion}
      modelPreview={modelPreview}
      variationOf={variationOf}
    />
  );
};

export default FloorPlanMachineCard;
